import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'autopod-sleep-timer';

interface SleepTimerState {
  endTime: number | null; // Unix timestamp when timer expires
}

function loadSleepTimer(): number | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const data: SleepTimerState = JSON.parse(stored);
    if (!data.endTime) return null;

    // Check if timer has already expired
    if (data.endTime <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return data.endTime;
  } catch {
    return null;
  }
}

function saveSleepTimer(endTime: number | null): void {
  try {
    if (endTime === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ endTime }));
    }
  } catch {
    // Ignore storage errors
  }
}

export interface UseSleepTimerReturn {
  /** Whether the sleep timer is active */
  isActive: boolean;
  /** Remaining time in seconds */
  remainingSeconds: number;
  /** Formatted remaining time (MM:SS or HH:MM:SS) */
  remainingFormatted: string;
  /** Set timer for specified minutes */
  setTimer: (minutes: number) => void;
  /** Cancel the active timer */
  cancelTimer: () => void;
}

export function useSleepTimer(onTimerEnd: () => void): UseSleepTimerReturn {
  const [endTime, setEndTime] = useState<number | null>(() => loadSleepTimer());
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const onTimerEndRef = useRef(onTimerEnd);

  // Keep callback ref updated
  useEffect(() => {
    onTimerEndRef.current = onTimerEnd;
  }, [onTimerEnd]);

  // Calculate remaining time and check for expiration
  useEffect(() => {
    if (!endTime) {
      setRemainingSeconds(0);
      return;
    }

    const updateRemaining = () => {
      const remaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
      setRemainingSeconds(remaining);

      if (remaining <= 0) {
        // Timer expired
        setEndTime(null);
        saveSleepTimer(null);
        onTimerEndRef.current();
      }
    };

    // Update immediately
    updateRemaining();

    // Update every second
    const interval = setInterval(updateRemaining, 1000);

    return () => clearInterval(interval);
  }, [endTime]);

  // Format remaining time
  const formatRemaining = useCallback((seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;

    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  }, []);

  const setTimer = useCallback((minutes: number) => {
    const newEndTime = Date.now() + minutes * 60 * 1000;
    setEndTime(newEndTime);
    saveSleepTimer(newEndTime);
  }, []);

  const cancelTimer = useCallback(() => {
    setEndTime(null);
    saveSleepTimer(null);
  }, []);

  return {
    isActive: endTime !== null,
    remainingSeconds,
    remainingFormatted: formatRemaining(remainingSeconds),
    setTimer,
    cancelTimer,
  };
}
